import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useApiQuery } from "../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { generateMapsLink } from "../../../common/infrastructure/routes/routePaths";
import {
  applyThemeColors,
  generateHexFromString,
  Default_Background_Primary_Color,
  Default_Background_Secondary_Color,
  Default_Button_Background_Color,
  Default_Button_Text_Color,
} from "../../../common/infrastructure/utilities/colorUtility";
import {
  IPublicStudioLinkPage,
  IPublicStudioLinkPageButton,
  IPublicStudioLinkPagePhone,
} from "../models/publicStudioLinkPageModels";
import { getLeadingEmoji, getMonogram } from "../utils/linkPageIconUtility";

/**
 * Everything the public link page needs to render itself: the studio's branding, its links with
 * their icons already resolved, and the contact details it chose to publish.
 */
export const usePublicStudioLinkPage = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data, isLoading, isError } = useApiQuery<IPublicStudioLinkPage>({
    queryKey: [endpointsMetadata.studioLinkPage.getPublicPage, slug],
    endpoint: `${endpointsMetadata.studioLinkPage.getPublicPage}/${slug}`,
    enabled: !!slug,
    globalIsLoading: false,
    retry: false,
  });

  const backgroundPrimary = generateHexFromString(
    data?.backgroundColorPrimary || Default_Background_Primary_Color,
  );
  const backgroundSecondary = generateHexFromString(
    data?.backgroundColorSecondary || Default_Background_Secondary_Color,
  );
  const themeButtonColor = generateHexFromString(
    data?.buttonBackgroundColor || Default_Button_Background_Color,
  );
  const buttonTextColor = generateHexFromString(
    data?.buttonTextColor || Default_Button_Text_Color,
  );

  // The API already resolves the fallback, but a payload from an older deploy would leave this
  // empty — and a blank color would paint every button transparent.
  const buttonColor = data?.linkPageButtonColor
    ? generateHexFromString(data.linkPageButtonColor)
    : themeButtonColor;

  // The page background is painted by #root (public/styles.css), which mixes a few percent of
  // the studio's colors into the dark app palette. Feeding those same CSS variables — instead
  // of painting a gradient here — is what makes this page read as the studio's own app rather
  // than a flat wash of its brand color. The Layout does the same for signed-in users; here the
  // colors come from the page's own payload, since an anonymous visitor has no selected studio.
  useEffect(() => {
    if (!data) {
      return;
    }

    applyThemeColors(
      backgroundPrimary,
      backgroundSecondary,
      themeButtonColor,
      buttonTextColor,
    );
  }, [
    data,
    backgroundPrimary,
    backgroundSecondary,
    themeButtonColor,
    buttonTextColor,
  ]);

  const items: IPublicStudioLinkPageButton[] = useMemo(
    () =>
      (data?.items ?? []).map((item) => ({
        name: item.name,
        url: item.url,
        iconUrl: item.iconUrl,
        // The label keeps whatever the studio typed, emoji included — the glyph is only the
        // fallback drawn inside the icon tile when there is no image.
        glyph: getLeadingEmoji(item.name) ?? getMonogram(item.name),
      })),
    [data?.items],
  );

  const phones: IPublicStudioLinkPagePhone[] = useMemo(() => {
    const candidates = [
      { number: data?.phoneNumber, description: data?.phoneNumberDescription },
      {
        number: data?.secondaryPhoneNumber,
        description: data?.secondaryPhoneNumberDescription,
      },
    ];

    return candidates
      .filter((phone) => !!phone.number?.trim())
      .map((phone) => ({
        number: phone.number!.trim(),
        description: phone.description?.trim() || null,
      }));
  }, [
    data?.phoneNumber,
    data?.phoneNumberDescription,
    data?.secondaryPhoneNumber,
    data?.secondaryPhoneNumberDescription,
  ]);

  // The studio's own link wins over a search by the address, and is enough on its own —
  // a pin that lands is worth a card even when the address text is not published.
  const mapsUrl = generateMapsLink(data?.address, data?.addressMapUrl) ?? null;

  return {
    isLoading,
    isError: isError || (!isLoading && !data),
    studioName: data?.studioName ?? "",
    logoUrl: data?.logoUrl ?? null,
    address: data?.address ?? null,
    mapsUrl,
    phones,
    hasContactCards: !!mapsUrl || phones.length > 0,
    items,
    hasItems: items.length > 0,
    buttonColor,
    buttonTextColor,
  };
};
